import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useStore, MAX_ITEMS } from "@/store/StoreContext";
import { imgSrc } from "@/lib/api";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeItem, totals, currency, priceOf, money } = useStore();
  const navigate = useNavigate();

  return (
    <>
      <div
        data-testid="cart-backdrop"
        onClick={() => setCartOpen(false)}
        className={`fixed inset-0 z-[70] bg-[#1C130B]/60 backdrop-blur-sm transition-opacity duration-300 ${cartOpen ? "opacity-100" : "pointer-events-none opacity-0"}`}
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col border-l border-[#E9DFC8] bg-[#FDF8F0] shadow-2xl transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${cartOpen ? "translate-x-0" : "translate-x-full"}`}
        aria-hidden={!cartOpen}
      >
        <div className="flex items-center justify-between border-b border-[#E9DFC8] px-6 py-5">
          <h2 className="font-display text-2xl font-semibold text-[#2B1E12]">Your Cart</h2>
          <button data-testid="cart-close" onClick={() => setCartOpen(false)} aria-label="Close cart"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#B08D2B]/35 text-[#6B5B45] transition-colors hover:bg-[#D4AF37]/20 hover:text-[#8F6E1D]">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {cart.length === 0 ? (
            <div data-testid="cart-empty" className="flex h-full flex-col items-center justify-center gap-4 text-center">
              <ShoppingBag className="h-10 w-10 text-[#B08D2B]/40" />
              <p className="font-display text-lg italic text-[#6B5B45]">Your cart is empty.<br />Continue your sacred journey below.</p>
              <button data-testid="cart-continue" onClick={() => setCartOpen(false)}
                className="border border-[#8F6E1D]/50 px-6 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#8F6E1D] transition-colors hover:bg-[#D4AF37] hover:text-[#1C130B]">
                Continue Shopping
              </button>
            </div>
          ) : (
            <ul className="space-y-5">
              {cart.map(({ product, quantity }) => (
                <li key={product.id} data-testid={`cart-item-${product.slug || product.id}`} className="flex gap-4 rounded-md border border-[#E9DFC8] bg-white p-3">
                  <img src={imgSrc(product.image)} alt={product.name} className="h-20 w-20 shrink-0 rounded-md bg-[#F7F0DF] object-contain p-1.5" />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold leading-snug text-[#2B1E12]">{product.name}</p>
                      <button data-testid={`cart-remove-${product.slug || product.id}`} onClick={() => removeItem(product.id)} aria-label="Remove"
                        className="text-[#6B5B45] transition-colors hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="mt-0.5 text-xs text-[#6B5B45]">{money(priceOf(product))} each</p>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-2 rounded-full border border-[#B08D2B]/30 px-1.5 py-1">
                        <button data-testid={`cart-minus-${product.slug || product.id}`} onClick={() => updateQty(product.id, quantity - 1)} aria-label="Decrease"
                          className="flex h-6 w-6 items-center justify-center rounded-full text-[#8F6E1D] transition-colors hover:bg-[#D4AF37]/25">
                          <Minus className="h-3 w-3" />
                        </button>
                        <span data-testid={`cart-qty-${product.slug || product.id}`} className="w-5 text-center text-sm font-semibold text-[#2B1E12]">{quantity}</span>
                        <button data-testid={`cart-plus-${product.slug || product.id}`} onClick={() => updateQty(product.id, quantity + 1)} aria-label="Increase"
                          className="flex h-6 w-6 items-center justify-center rounded-full text-[#8F6E1D] transition-colors hover:bg-[#D4AF37]/25">
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <span className="font-display text-lg font-bold text-[#8F6E1D]">{money(priceOf(product) * quantity)}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-[#E9DFC8] bg-white px-6 py-5">
            <p className="mb-4 text-[11px] uppercase tracking-[0.14em] text-[#6B5B45]">
              {cart.length}/{MAX_ITEMS} products · {currency === "USD" ? "$40 international delivery, once per order" : "free shipping across India"}
            </p>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between text-[#6B5B45]">
                <dt>Subtotal</dt><dd data-testid="cart-subtotal">{money(totals.subtotal)}</dd>
              </div>
              <div className="flex justify-between text-[#6B5B45]">
                <dt>Shipping</dt><dd data-testid="cart-shipping" className={totals.shipping === 0 && cart.length > 0 ? "font-semibold text-green-700" : ""}>{totals.shipping === 0 && cart.length > 0 ? "FREE" : money(totals.shipping)}</dd>
              </div>
              <div className="flex justify-between border-t border-[#E9DFC8] pt-2 text-base font-bold text-[#2B1E12]">
                <dt>Total</dt><dd data-testid="cart-total" className="font-display text-xl text-[#8F6E1D]">{money(totals.total)}</dd>
              </div>
            </dl>
            <button
              data-testid="proceed-to-checkout"
              onClick={() => { setCartOpen(false); navigate("/checkout"); }}
              className="mt-5 w-full bg-[#D4AF37] py-4 text-xs font-bold uppercase tracking-[0.22em] text-[#1C130B] transition-colors duration-300 hover:bg-[#8F6E1D] hover:text-white"
            >
              Proceed to Checkout
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
